import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, USER_ID } from "../App";
import { 
  ArrowRight, 
  User as UserIcon,
  Lightning,
  Target,
  CheckCircle
} from "@phosphor-icons/react";

const STEPS = [
  { id: "gender", title: "Ton programme commence ici", subtitle: "Choisis le profil qui te ressemble" },
  { id: "age", title: "Quel est ton âge ?", subtitle: "On adapte l'intensité à ton profil" },
  { id: "objective", title: "Quel est ton objectif ?", subtitle: "On personnalise ton parcours" },
  { id: "zones", title: "Quelles zones veux-tu cibler ?", subtitle: "Sélectionne toutes les zones à améliorer" },
  { id: "level", title: "Quel est ton niveau ?", subtitle: "On ajuste l'intensité pour progresser sans te brûler" },
  { id: "metrics", title: "Tes mensurations", subtitle: "Pour suivre ta progression" },
];

const MALE_IMG = "https://static.prod-images.emergentagent.com/jobs/0244f962-b610-49f9-adb1-07a23004887d/images/68c2349a476f4fd22664f84d8fae44b949cb0fd96c229be01779f63919cae861.png";
const FEMALE_IMG = "https://static.prod-images.emergentagent.com/jobs/0244f962-b610-49f9-adb1-07a23004887d/images/11a194782ddad3b0d4b747ce53ba6279dd75fb0df89c56bb153b750c881d4fbf.png";

const Onboarding = ({ setProfile }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    gender: null,
    age_range: null,
    objective: null,
    target_zones: [],
    fitness_level: null,
    weight: "",
    height: "",
    goal_weight: "",
  });
  const [saving, setSaving] = useState(false);

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const handleNext = async () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Save profile
      setSaving(true);
      try {
        const profileData = {
          ...formData,
          weight: formData.weight ? parseFloat(formData.weight) : null,
          height: formData.height ? parseFloat(formData.height) : null,
          goal_weight: formData.goal_weight ? parseFloat(formData.goal_weight) : null,
          daily_calorie_goal: calculateCalories(formData),
          daily_protein_goal: calculateProtein(formData),
          daily_carbs_goal: 250,
          daily_fat_goal: 65,
          onboarding_complete: true,
        };

        const response = await axios.post(`${API}/profile`, {
          ...profileData,
          id: USER_ID,
        });
        
        setProfile(response.data);
        navigate("/dashboard");
      } catch (error) {
        console.error("Error saving profile:", error);
      } finally {
        setSaving(false);
      }
    }
  };

  const calculateCalories = (data) => {
    let base = 2000;
    if (data.gender === "male") base = 2200;
    if (data.objective === "lose") base -= 300;
    if (data.objective === "gain") base += 300;
    if (data.fitness_level === "advanced") base += 200;
    return base;
  };

  const calculateProtein = (data) => {
    const weight = parseFloat(data.weight) || 70;
    let multiplier = 1.6;
    if (data.objective === "gain") multiplier = 2.0;
    if (data.fitness_level === "advanced") multiplier = 2.2;
    return Math.round(weight * multiplier);
  };

  const canProceed = () => {
    switch (STEPS[currentStep].id) {
      case "gender": return formData.gender;
      case "age": return formData.age_range;
      case "objective": return formData.objective;
      case "zones": return formData.target_zones.length > 0;
      case "level": return formData.fitness_level;
      case "metrics": return formData.weight && formData.height;
      default: return true;
    }
  };

  const toggleZone = (zone) => {
    setFormData(prev => ({
      ...prev,
      target_zones: prev.target_zones.includes(zone)
        ? prev.target_zones.filter(z => z !== zone)
        : [...prev.target_zones, zone]
    }));
  };

  const renderStep = () => {
    switch (STEPS[currentStep].id) {
      case "gender":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <button
              onClick={() => setFormData({ ...formData, gender: "female" })}
              className={`relative overflow-hidden rounded-2xl transition-all duration-300 ${
                formData.gender === "female" ? "ring-4 ring-gold" : "hover:shadow-lg"
              }`}
              data-testid="select-female"
            >
              <div className="w-full h-[30rem] bg-[linear-gradient(180deg,#fff8f5_0%,#ffe9df_100%)] p-4">
                <img 
                  src={FEMALE_IMG} 
                  alt="Femme" 
                  className="w-full h-full object-contain object-center"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                <h3 className="text-white font-display text-2xl">Femme</h3>
                <p className="text-white/80 text-sm mt-1">Un parcours plus personnel</p>
              </div>
              {formData.gender === "female" && (
                <div className="absolute top-4 right-4 w-8 h-8 bg-gold rounded-full flex items-center justify-center">
                  <CheckCircle size={20} weight="fill" className="text-white" />
                </div>
              )}
            </button>

            <button
              onClick={() => setFormData({ ...formData, gender: "male" })}
              className={`relative overflow-hidden rounded-2xl transition-all duration-300 ${
                formData.gender === "male" ? "ring-4 ring-gold" : "hover:shadow-lg"
              }`}
              data-testid="select-male"
            >
              <div className="w-full h-[30rem] bg-[linear-gradient(180deg,#f7f8fb_0%,#e7edf6_100%)] p-4">
                <img 
                  src={MALE_IMG} 
                  alt="Homme" 
                  className="w-full h-full object-contain object-center"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                <h3 className="text-white font-display text-2xl">Homme</h3>
                <p className="text-white/80 text-sm mt-1">Pensée pour tes objectifs</p>
              </div>
              {formData.gender === "male" && (
                <div className="absolute top-4 right-4 w-8 h-8 bg-gold rounded-full flex items-center justify-center">
                  <CheckCircle size={20} weight="fill" className="text-white" />
                </div>
              )}
            </button>
          </div>
        );

      case "age":
        const ageRanges = ["18-24", "25-35", "36-45", "46-55", "56+"];
        return (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-3xl mx-auto">
            {ageRanges.map((range) => (
              <button
                key={range}
                onClick={() => setFormData({ ...formData, age_range: range })}
                className={`p-6 rounded-2xl border-2 transition-all duration-200 ${
                  formData.age_range === range
                    ? "border-gold bg-gold-light"
                    : "border-gray-200 bg-white hover:border-gold"
                }`}
                data-testid={`age-${range}`}
              >
                <span className="text-2xl font-bold text-charcoal">{range}</span>
                <span className="block text-xs text-gray-400 mt-1 uppercase tracking-wider">ans</span>
              </button>
            ))}
          </div>
        );

      case "objective":
        const objectives = [
          { id: "tone", label: "Te tonifier et gagner en muscle", icon: "💪" },
          { id: "fit", label: "Te remettre en forme", icon: "🏃" },
          { id: "health", label: "Améliorer ta santé", icon: "❤️" },
          { id: "lose", label: "Perdre du poids", icon: "⚖️" },
          { id: "gain", label: "Prendre de la masse", icon: "🎯" },
        ];
        return (
          <div className="space-y-4 max-w-xl mx-auto">
            {objectives.map((obj) => (
              <button
                key={obj.id}
                onClick={() => setFormData({ ...formData, objective: obj.id })}
                className={`w-full p-5 rounded-2xl border-2 text-left transition-all duration-200 flex items-center gap-4 ${
                  formData.objective === obj.id
                    ? "border-gold bg-gold-light"
                    : "border-gray-200 bg-white hover:border-gold"
                }`}
                data-testid={`objective-${obj.id}`}
              >
                <span className="text-3xl">{obj.icon}</span>
                <span className="text-lg font-medium text-charcoal">{obj.label}</span>
              </button>
            ))}
          </div>
        );

      case "zones":
        const zones = ["Pectoraux", "Abdominaux", "Bras", "Dos", "Jambes", "Épaules"];
        return (
          <div className="max-w-xl mx-auto">
            <div className="grid grid-cols-2 gap-4">
              {zones.map((zone) => (
                <button
                  key={zone}
                  onClick={() => toggleZone(zone)}
                  className={`p-5 rounded-2xl border-2 transition-all duration-200 ${
                    formData.target_zones.includes(zone)
                      ? "border-gold bg-gold-light"
                      : "border-gray-200 bg-white hover:border-gold"
                  }`}
                  data-testid={`zone-${zone.toLowerCase()}`}
                >
                  <span className="text-lg font-medium text-charcoal">{zone}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case "level":
        const levels = [
          { id: "beginner", label: "Débutant", desc: "Tu t'entraînes rarement ou tu démarres à peine", icon: "⚡" },
          { id: "intermediate", label: "Intermédiaire", desc: "Tu t'entraînes 1 à 2 fois par semaine", icon: "⚡⚡" },
          { id: "advanced", label: "Avancé", desc: "Tu t'entraînes régulièrement depuis longtemps", icon: "⚡⚡⚡" },
        ];
        return (
          <div className="space-y-4 max-w-xl mx-auto">
            {levels.map((level) => (
              <button
                key={level.id}
                onClick={() => setFormData({ ...formData, fitness_level: level.id })}
                className={`w-full p-5 rounded-2xl border-2 text-left transition-all duration-200 ${
                  formData.fitness_level === level.id
                    ? "border-gold bg-gold-light"
                    : "border-gray-200 bg-white hover:border-gold"
                }`}
                data-testid={`level-${level.id}`}
              >
                <div className="flex items-start gap-4">
                  <span className="text-xl">{level.icon}</span>
                  <div>
                    <span className="text-lg font-medium text-charcoal block">{level.label}</span>
                    <span className="text-sm text-gray-500">{level.desc}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        );

      case "metrics":
        return (
          <div className="max-w-md mx-auto space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Poids actuel (kg)</label>
              <input
                type="number"
                value={formData.weight}
                onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                placeholder="Ex: 75"
                className="w-full p-4 rounded-xl border-2 border-gray-200 focus:border-gold text-lg"
                data-testid="input-weight"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Taille (cm)</label>
              <input
                type="number"
                value={formData.height}
                onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                placeholder="Ex: 175"
                className="w-full p-4 rounded-xl border-2 border-gray-200 focus:border-gold text-lg"
                data-testid="input-height"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Poids objectif (kg) - Optionnel</label>
              <input
                type="number"
                value={formData.goal_weight}
                onChange={(e) => setFormData({ ...formData, goal_weight: e.target.value })}
                placeholder="Ex: 70"
                className="w-full p-4 rounded-xl border-2 border-gray-200 focus:border-gold text-lg"
                data-testid="input-goal-weight"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="flex items-center justify-between p-4 md:p-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-charcoal rounded-xl flex items-center justify-center shadow-[0_10px_25px_rgba(0,0,0,0.12)]">
            <span className="text-white font-bold text-lg">M</span>
          </div>
          <div>
            <span className="font-display text-2xl uppercase text-charcoal">myfitbody.fr</span>
            <span className="text-xs text-gray-400 block mt-0.5 uppercase tracking-[0.24em]">programme personnalisé</span>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500 uppercase tracking-wider">Progression</span>
          <span className="text-sm font-bold text-charcoal">{Math.round(progress)}%</span>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="px-4 md:px-6">
        <div className="progress-bar">
          <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Content */}
      <div className="px-4 md:px-6 py-8 md:py-12">
        <div className="text-center mb-8 md:mb-12 animate-fadeIn">
          <h1 className="onboarding-title">{STEPS[currentStep].title}</h1>
          <p className="onboarding-subtitle">{STEPS[currentStep].subtitle}</p>
        </div>

        <div className="animate-slideUp">
          {renderStep()}
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 p-4 md:p-6 bg-gradient-to-t from-cream via-cream to-transparent">
        <div className="max-w-xl mx-auto">
          <button
            onClick={handleNext}
            disabled={!canProceed() || saving}
            className={`w-full py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 transition-all duration-200 ${
              canProceed() && !saving
                ? "bg-gold text-charcoal hover:bg-gold-hover"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
            data-testid="continue-button"
          >
            {saving ? "Création du profil..." : currentStep === STEPS.length - 1 ? "Commencer" : "Continuer"}
            {!saving && <ArrowRight size={20} weight="bold" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
